import { VariantsEntity } from 'features/product-info';
import { ReviewData, ReviewsPaginationPage } from '../types';

type ProductVariantMap = Record<string, { label: string; link: string }>;

const isVariantsModelUsable = (variantsModel: VariantsEntity | null): variantsModel is VariantsEntity =>
  Boolean(variantsModel?.products?.length && variantsModel?.axisList?.length);

const createProductVariantMap = (variantsModel: VariantsEntity | null): ProductVariantMap | null => {
  if (!isVariantsModelUsable(variantsModel)) return null;

  const axisNameByAttribute = new Map(variantsModel.axisList.map((axis) => [axis.attribute, axis.name]));

  const productVariantMap: ProductVariantMap = {};

  for (const product of variantsModel.products) {
    const parts: string[] = [];

    for (const { id, axis } of product.axesValues) {
      const axisValue = axisNameByAttribute.get(id);
      if (axisValue) {
        parts.push(`${axisValue}: ${axis}`);
      }
    }

    productVariantMap[product.productId] = {
      label: parts.join(', '),
      link: product.productLink,
    };
  }

  return productVariantMap;
};

const enrichReviewsWithVariantMap = (
  reviewsCards: ReviewData[],
  productVariantMap: ProductVariantMap,
): ReviewData[] =>
  reviewsCards.map((review) => {
    const productId = review.productReviewId || review.reviewId;
    const variantInfo = productVariantMap[productId];

    return {
      ...review,
      variant: {
        string: variantInfo?.label || '',
        link: variantInfo?.link || '',
        productId,
      },
    };
  });

export const enrichReviewsWithVariants = (
  reviewsCards: ReviewData[],
  variantsModel: VariantsEntity | null,
): ReviewData[] => {
  const productVariantMap = createProductVariantMap(variantsModel);
  if (!productVariantMap) return reviewsCards;
  return enrichReviewsWithVariantMap(reviewsCards, productVariantMap);
};

export const enrichReviewsWithVariantsPagination = (
  reviewsCardsPagination: ReviewsPaginationPage[],
  variantsModel: VariantsEntity | null,
): ReviewsPaginationPage[] => {
  const productVariantMap = createProductVariantMap(variantsModel);
  if (!productVariantMap) return reviewsCardsPagination;

  return reviewsCardsPagination.map((page) => ({
    ...page,
    reviewsCards: enrichReviewsWithVariantMap(page.reviewsCards, productVariantMap),
  }));
};
